/**
 * AnomalyActionHero — the "gasp moment" card.
 *
 * Pulls /v1/anomalies/with-actions which returns AI-generated narrative +
 * proposed action for each statistical anomaly. Renders as a featured hero
 * card at the top of the dashboard: WHAT changed, WHY, what to DO, and a
 * link to the page where the flagged items live.
 *
 * Show conditions:
 *   - card hidden when no anomalies (statistical control = no drama)
 *   - up to 2 stacked cards if multiple anomalies (sorted by severity)
 *
 * This is the surface that converts "you have a dashboard" into "your AI
 * just caught something a human couldn't."
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useBasePath } from '@/lib/config/nav-helpers';

interface ProposedAction {
  kind: 'send_emails' | 'investigate';
  label: string;
  targets: string[];
  impact: string;
}

interface AnomalyCard {
  metricId: string;
  label: string;
  severity: 'high' | 'medium';
  direction: 'up' | 'down';
  zScore: number;
  rawMessage: string;
  narrative: string;
  proposedAction: ProposedAction;
  generatedAt: string;
}

interface AnomalyCardsResponse {
  cards: AnomalyCard[];
  generatedAt: string;
}

const KIND_META: Record<ProposedAction['kind'], { icon: string; tint: string }> = {
  send_emails:    { icon: '✉',  tint: 'bg-primary/10 text-primary border-primary/30' },
  investigate:    { icon: '🔍',  tint: 'bg-ink-muted/10 text-ink-muted border-border' },
};

// ponytail: nothing executes from this card, so its one button goes where the
// flagged items already live. An approve button comes back only when approving
// actually drafts or sends something.
const DESTINATION: Record<string, { label: string; path: string }> = {
  weekly_revenue:         { label: 'Go to Finance',  path: 'revenue' },  // nav calls /revenue "Finance"
  weekly_invoices_issued: { label: 'Go to Finance',  path: 'revenue' },
  weekly_deals_won:       { label: 'Go to Pipeline', path: 'pipeline' },
};

export function AnomalyActionHero() {
  const { data, isLoading } = useQuery({
    queryKey: ['anomalies-with-actions'],
    queryFn: () => api<AnomalyCardsResponse>('/v1/anomalies/with-actions'),
    staleTime: 5 * 60_000, // 5min — narrative is expensive (Claude call)
  });

  if (isLoading || !data || data.cards.length === 0) return null;

  return (
    <div className="space-y-3">
      {data.cards.map((card) => (
        <AnomalyCardRow key={card.metricId} card={card} />
      ))}
    </div>
  );
}

function AnomalyCardRow({ card }: { card: AnomalyCard }) {
  const [dismissed, setDismissed] = useState(false);
  const basePath = useBasePath();
  const meta = KIND_META[card.proposedAction.kind];
  const dest = DESTINATION[card.metricId];

  if (dismissed) return null;

  const severityClasses = card.severity === 'high'
    ? 'border-danger/40 bg-danger/3'
    : 'border-warn/40 bg-warn/3';

  return (
    <div className={`rounded-xl border-2 ${severityClasses} p-4 sm:p-5`}>
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className={`shrink-0 h-10 w-10 rounded-lg ${card.severity === 'high' ? 'bg-danger/20 text-danger' : 'bg-warn/20 text-warn'} flex items-center justify-center text-lg font-semibold`}>
          {card.direction === 'up' ? '↑' : '↓'}
        </div>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className={`text-2xs font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${card.severity === 'high' ? 'bg-danger/20 text-danger' : 'bg-warn/20 text-warn'}`}>
              Anomaly detected · {card.severity}
            </span>
            <span className="text-2xs font-semibold text-ink">{card.label}</span>
            <span className="font-mono text-2xs text-ink-dim tabular-nums">{Math.abs(card.zScore).toFixed(1)}σ</span>
          </div>

          {/* Narrative */}
          <p className="text-sm text-ink leading-relaxed mb-3">
            {card.narrative}
          </p>

          {/* Proposed action */}
          <div className={`rounded-lg border ${meta.tint} px-3 py-2.5 mb-3`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base leading-none">{meta.icon}</span>
              <span className="text-2xs font-semibold uppercase tracking-wider">Waypoint proposes</span>
            </div>
            <div className="text-sm font-medium text-ink leading-snug">{card.proposedAction.label}</div>
            {card.proposedAction.targets.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {card.proposedAction.targets.slice(0, 4).map((t) => (
                  <span key={t} className="text-2xs px-1.5 py-0.5 rounded-sm bg-bg-0 border border-border text-ink-muted">
                    {t}
                  </span>
                ))}
              </div>
            )}
            {card.proposedAction.impact && (
              <div className="text-2xs text-success font-medium mt-2">→ {card.proposedAction.impact}</div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {dest && (
              <Link
                to={`${basePath}/${dest.path}`}
                className="rounded-md bg-primary text-white px-3.5 py-1.5 text-xs font-semibold shadow-xs hover:opacity-90"
              >
                {dest.label}
              </Link>
            )}
            <button
              onClick={() => setDismissed(true)}
              className="rounded-md border border-border bg-bg-0 px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-surface-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
